///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2021, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2021 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { BaseLoader } from "./BaseLoader";

export class TCSLoader extends BaseLoader {
  override async load(): Promise<void> {
    if (!this.viewer.visualizeJs) return;

    const visLib = this.viewer.visLib();
    const visViewer = visLib.getViewer();
    const abortController = new AbortController();

    const listFileForDownload = [this.model.database, ...this.model.geometry];
    const chunksProgress = listFileForDownload.map(() => 0);

    const calcProgress = (index, progress) => {
      chunksProgress[index] = progress;
      const fileProgress = chunksProgress.reduce((acc, progress) => (acc += progress)) / (chunksProgress.length || 1);
      this.viewer.emitEvent({ type: "geometryprogress", data: fileProgress, model: this.model });
    };

    this.viewer._abortController = abortController;
    this.viewer.emitEvent({ type: "geometrystart", model: this.model });

    try {
      for (let i = 0; i < listFileForDownload.length; i++) {
        const chunk = listFileForDownload[i];

        const progressCb = (progress) => calcProgress(i, progress);
        const arrayBuffer = await this.model.downloadResource(chunk, progressCb, abortController.signal);

        if (abortController.signal.aborted) {
          await Promise.reject(new Error(`Open model aborted ${this.model.name}`));
        }

        visViewer.parseStream(new Uint8Array(arrayBuffer));
        this.viewer.update();

        const data = new Uint8Array(arrayBuffer);

        if (i === 0) {
          this.viewer.update(true);

          this.viewer.syncOpenCloudVisualStyle(false);
          this.viewer.syncOptions();

          this.viewer.emitEvent({ type: "databasechunk", data, model: this.model });
        } else {
          this.viewer.emitEvent({ type: "geometrychunk", data, model: this.model });
        }
      }
      this.viewer.emitEvent({ type: "geometryend", model: this.model });
    } catch (error) {
      this.viewer.emitEvent({ type: "geometryerror", data: error, model: this.model });
      throw error;
    }
  }
}
